import { z } from "zod";

/**
 * Taking blacklisted leads off the blacklist to contact them (PREC-2548). Mirrors the API's
 * `BlacklistOverrideVerdict` / `BlacklistOverrideResult` (`apps/api/src/blacklist/override/`):
 * the API decides per lead what may be removed, the web only asks and reports.
 */

/**
 * Why a lead cannot be taken off the blacklist from the product: the lead unsubscribed (a
 * compliance record), or a Signalist-wide entry blocks them (ours, not the customer's).
 */
export const BlacklistOverrideRefusalSchema = z.enum(["UNSUBSCRIBED", "GLOBAL"]);

/** What taking one lead off the blacklist would take. */
export const BlacklistOverrideVerdictSchema = z.object({
	listPersonId: z.number(),
	name: z.string(),
	companyName: z.string().nullable(),
	refusal: BlacklistOverrideRefusalSchema.nullable(),
	/** Entries naming this person; removed on confirm. */
	personEntryIds: z.array(z.number()),
	/** Entries blocking the whole company; removed only with the user's explicit consent. */
	companyEntryIds: z.array(z.number()),
	/** The company is a competitor; it stays one, and only this person is exempted. */
	competitorEntryIds: z.array(z.number()),
});

export const BlacklistOverrideVerdictListSchema = z.array(BlacklistOverrideVerdictSchema);

/** Why the API left a lead on the blacklist. */
export const BlacklistOverrideSkipReasonSchema = z.enum([
	"UNSUBSCRIBED",
	"GLOBAL",
	"COMPANY_NOT_REMOVED",
]);

export const BlacklistOverrideSkipSchema = z.object({
	listPersonId: z.number(),
	name: z.string(),
	reason: BlacklistOverrideSkipReasonSchema,
});

export const BlacklistOverrideResultSchema = z.object({
	overriddenIds: z.array(z.number()),
	skipped: z.array(BlacklistOverrideSkipSchema),
});

export const BlacklistOverridePreviewRequestSchema = z.object({
	listPersonIds: z.array(z.number()),
});

export const BlacklistOverrideApplyRequestSchema = BlacklistOverridePreviewRequestSchema.extend({
	/** Also remove the company-wide entries that block these leads. */
	removeCompany: z.boolean().optional(),
	/** The bucket the leads move to; Found by default. */
	inclusionStatus: z.enum(["ADDED", "UNSURE"]).optional(),
});

export type BlacklistOverrideRefusal = z.infer<typeof BlacklistOverrideRefusalSchema>;
export type BlacklistOverrideVerdict = z.infer<typeof BlacklistOverrideVerdictSchema>;
export type BlacklistOverrideSkipReason = z.infer<typeof BlacklistOverrideSkipReasonSchema>;
export type BlacklistOverrideSkip = z.infer<typeof BlacklistOverrideSkipSchema>;
export type BlacklistOverrideResult = z.infer<typeof BlacklistOverrideResultSchema>;
export type BlacklistOverridePreviewRequest = z.infer<typeof BlacklistOverridePreviewRequestSchema>;
export type BlacklistOverrideApplyRequest = z.infer<typeof BlacklistOverrideApplyRequestSchema>;
